import {
  Badge,
  Button,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { usePlanningStore } from '@tom-ui/planning'
import { IssueStatus } from '@tom-ui/planning/app/api'
import { ChipWithOptions, FilterIcon, Header, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { AggregateIssuesViewStore } from '../../Stores/AggregateIssuesViewStore'
import { IssuesFilterDrawer } from './IssuesFilterDrawer'

type Props = {
  store: AggregateIssuesViewStore
}

export const IssuesPageHeader: FC<Props> = observer(props => {
  const { store } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const { drawerStore } = usePlanningStore()

  return (
    <>
      <Header title={t('issues', 'Issues')} />
      <Stack flexDirection='column' gap={2} sx={{ padding: theme.customSpacing.m }}>
        <Stack flexDirection='row' alignItems='center' gap={2}>
          <Stack flex={1}>
            <TextField
              fullWidth
              label={t('searchContainerNumber', 'Search container number')}
              sx={{ width: '100%', marginTop: 0 }}
              data-cy='issues-page-search-field'
              value={store.containerNumberFilter ?? ''}
              onChange={event => {
                store.setContainerNumberFilter(event.currentTarget.value.toUpperCase())
              }}
            />
          </Stack>
          <Stack>
            <IconButton
              onClick={() => drawerStore.showView(<IssuesFilterDrawer store={store} />)}
              size='small'
              sx={{ ml: 2 }}
            >
              <Badge color='primary' variant='dot' invisible={!store.isFilterEnabled}>
                <FilterIcon />
              </Badge>
            </IconButton>
          </Stack>
        </Stack>
        <IssuesFilterControl {...props} />
      </Stack>
    </>
  )
})

const IssuesFilterControl: FC<Props> = observer(({ store }) => {
  const { t } = useTranslate()

  const statusOptions = computed(() => [
    {
      text: t('all', 'All'),
      count: store.issuesCountByStatus.all,
      key: 'all',
    },
    {
      text: t('unresolved', 'Unresolved'),
      count: store.issuesCountByStatus.unresolved,
      key: IssueStatus.Unresolved,
    },
    {
      text: t('needFollowUp', 'Need follow up'),
      count: store.issuesCountByStatus.followUp,
      key: IssueStatus.FollowUp,
    },
  ]).get()

  const selectedLabel = statusOptions.find(
    o => o.key === (store.selectedIssueStatus ?? 'all'),
  )?.text

  return (
    <Stack flexDirection='row' alignItems='center' gap={2}>
      <ChipWithOptions
        value={selectedLabel}
        label={t('status', 'Status')}
        options={statusOptions.map(option => (
          <MenuItem
            key={option.key}
            selected={option.key === store.selectedIssueStatus}
            value={option.key}
            onClick={() => {
              if (option.key === 'all') {
                store.setSelectedIssueStatus()
              } else {
                store.setSelectedIssueStatus(option.key as IssueStatus)
              }
            }}
          >
            <ListItemText
              primary={option.text}
              slotProps={{
                primary: { variant: 'body2' },
              }}
            />
            <Typography variant='body2' color='secondary'>
              {option.count}
            </Typography>
          </MenuItem>
        ))}
      />
      {(store.selectedIssueStatus || store.selectedIssueType || store.containerNumberFilter) && (
        <Button color='secondary' onClick={() => store.clearFilters()}>
          {t('clearAll', 'Clear all')}
        </Button>
      )}
    </Stack>
  )
})
